import React, { useEffect, useMemo, useRef, useState } from "react";
import type { PopupWheelContent, PopupWheelSegment } from "@/shared/types/popup-types";
import type { ResolvedStepDesign } from "@/shared/types/popup-design";
import type { WheelStatus } from "./WheelStage";
import { segmentFill, contrastText } from "@/lib/wheel";

interface PlinkoStageProps {
  wheel: PopupWheelContent;
  segments: PopupWheelSegment[];
  step: ResolvedStepDesign;
  panelIsLight: boolean;
  status: WheelStatus;
  resultIndex: number | null;
  onLanded?: (index: number) => void;
  onSettled?: () => void;
  inert?: boolean;
}

const W = 400;
const H = 520;
const BALL_R = 11;
const SLOT_H = 54;

const reducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Plinko board — the wheel's second game. Same segments (= prize slots along
 * the bottom), same server-decided outcome, same status lifecycle: the ball
 * free-falls while the submission is in flight and steers into the slot the
 * server named once the result arrives. Nothing here decides a prize.
 */
export const PlinkoStage: React.FC<PlinkoStageProps> = ({
  wheel,
  segments,
  step,
  panelIsLight,
  status,
  resultIndex,
  onLanded,
  onSettled,
  inert = false,
}) => {
  const rows = Math.min(14, Math.max(4, wheel.plinko_rows ?? 8));
  const slots = Math.max(1, segments.length);
  const pegColor = wheel.plinko_peg_color || (panelIsLight ? "#111827" : "#ffffff");
  const ballColor = wheel.plinko_ball_color || (wheel.pointer_color || "#f43f5e");
  const topY = 60;
  const boardH = H - SLOT_H - topY - 20;
  const rowGap = boardH / rows;
  const slotW = W / slots;

  // Peg grid: alternating offsets, like a real board.
  const pegs = useMemo(() => {
    const out: { x: number; y: number }[] = [];
    const cols = slots + 1;
    for (let r = 0; r < rows; r++) {
      const shift = (r % 2) * (slotW / 2);
      for (let c = 0; c <= cols; c++) {
        const x = c * slotW - slotW / 2 + shift;
        if (x < 6 || x > W - 6) continue;
        out.push({ x, y: topY + r * rowGap });
      }
    }
    return out;
  }, [rows, slots, slotW, rowGap]);

  const [ball, setBall] = useState<{ x: number; y: number } | null>(null);
  const [landed, setLanded] = useState<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const landedRef = useRef(false);
  const startRef = useRef(0);

  // Reset for a fresh drop
  useEffect(() => {
    if (status === "idle") {
      setBall(null);
      setLanded(null);
      landedRef.current = false;
    }
  }, [status]);

  useEffect(() => {
    if (inert || status === "idle") return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const slotX = (i: number) => i * slotW + slotW / 2;
    const targetX = resultIndex != null ? slotX(Math.min(slots - 1, Math.max(0, resultIndex))) : null;
    const fallMs = Math.max(1500, Math.min(6000, wheel.spin_duration_ms ?? 3200));
    const endY = H - SLOT_H / 2;
    const startX = W / 2;

    if (reducedMotion() && status === "result" && targetX != null) {
      setBall({ x: targetX, y: endY });
      if (!landedRef.current) {
        landedRef.current = true;
        setLanded(resultIndex);
        onLanded?.(resultIndex as number);
      }
      return;
    }
    if (reducedMotion() && status === "error") {
      setBall(null);
      onSettled?.();
      return;
    }

    startRef.current = performance.now();
    // Deterministic wobble seeds so replays look alive but not random-jumpy
    const seed = resultIndex != null ? resultIndex * 7 + 3 : 5;
    const tick = (now: number) => {
      const t = Math.min(1, (now - startRef.current) / fallMs);
      const y = topY - 30 + (endY - (topY - 30)) * (t * t * 0.7 + t * 0.3);
      // Bounce off pegs: sway per row, converging on the target column when known
      const row = Math.floor((y - topY) / rowGap);
      const sway = Math.sin((row + seed) * 1.7) * (slotW / 2) * (1 - t * 0.6);
      const driftTarget = targetX ?? startX;
      const x = startX + (driftTarget - startX) * (t < 0.55 ? 0 : (t - 0.55) / 0.45) + sway * (1 - t);
      setBall({ x: Math.max(BALL_R, Math.min(W - BALL_R, x)), y });
      if (t < 1) {
        // Free-fall with no result yet: loop the top half until the server answers.
        if (targetX == null && t > 0.5 && status === "spinning") {
          startRef.current = now - fallMs * 0.2;
        }
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      if (status === "result" && resultIndex != null && !landedRef.current) {
        landedRef.current = true;
        setLanded(resultIndex);
        onLanded?.(resultIndex);
      } else if (status === "error") {
        setBall(null);
        onSettled?.();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, resultIndex, inert]);

  const labelFs = Math.max(9, Math.min(14, 46 / Math.max(3, slots)));

  return (
    <div
      className="flowapt-plinko"
      style={{ width: `min(88vw, ${wheel.size && wheel.size > 0 ? wheel.size : 340}px)`, margin: "0 auto" }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Plinko board">
        {pegs.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={4} fill={pegColor} opacity={0.9} />
        ))}
        {/* Slots */}
        {segments.map((seg, i) => {
          const fill = segmentFill(seg, i, wheel.palette);
          const active = landed === i;
          return (
            <g key={seg.id}>
              <rect
                x={i * slotW + 3}
                y={H - SLOT_H}
                width={slotW - 6}
                height={SLOT_H}
                rx={6}
                fill={fill}
                opacity={landed == null || active ? 1 : 0.45}
                stroke={active ? "#ffffff" : "none"}
                strokeWidth={active ? 3 : 0}
              />
              <text
                x={i * slotW + slotW / 2}
                y={H - SLOT_H / 2 + labelFs / 3}
                textAnchor="middle"
                fontSize={labelFs}
                fontWeight={700}
                fill={seg.text_color || contrastText(fill)}
                style={{ fontFamily: "inherit" }}
              >
                {seg.label}
              </text>
            </g>
          );
        })}
        {ball && (
          <circle cx={ball.x} cy={ball.y} r={BALL_R} fill={ballColor} stroke="#ffffff" strokeWidth={2} />
        )}
      </svg>
    </div>
  );
};

export default PlinkoStage;
